import 'theme';

class NwpProductGalleryController extends HTMLElement {
  #carousel;
  #abort;
  #observer;
  #mutations;
  #selected;
  #requested;
  #frame;
  #pinch;
  #lightbox;
  #onViewerOpen;
  #onViewerClose;
  #viewerOpen = false;

  connectedCallback() {
    this.#carousel = this.closest('product-gallery')?.querySelector('.product-gallery__carousel');
    if (!this.#carousel) return;
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const schedule = () => this.#schedule();
    this.#carousel.addEventListener('carousel:select', (event) => {
      this.#requested = event.detail?.cell ?? null;
      schedule();
    }, options);
    this.#carousel.addEventListener('carousel:settle', (event) => {
      if (!this.#requested || event.detail?.cell === this.#requested) this.#requested = null;
      schedule();
    }, options);
    for (const event of ['carousel:change', 'carousel:filter']) {
      this.#carousel.addEventListener(event, schedule, options);
    }
    // A direct gesture supersedes any earlier programmatic request. Resize and
    // stale scrollend events do not, so the requested cell remains authoritative.
    this.#carousel.addEventListener('pointerdown', () => { this.#requested = null; }, options);
    this.#carousel.addEventListener('load', schedule, { ...options, capture: true });
    this.#carousel.addEventListener('keydown', (event) => this.#onKeydown(event), options);
    this.#carousel.addEventListener('click', (event) => {
      const button = event.target.closest('[data-nwp-gallery-zoom]');
      if (!button || event.defaultPrevented || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      this.#openImage(button.closest('.product-gallery__media'), button);
    }, options);
    this.#carousel.addEventListener('touchstart', (event) => this.#onTouchStart(event), { ...options, passive: false });
    this.#carousel.addEventListener('touchmove', (event) => this.#onTouchMove(event), { ...options, passive: false });
    for (const type of ['touchend', 'touchcancel']) {
      this.#carousel.addEventListener(type, (event) => this.#onTouchEnd(event), { ...options, passive: false });
    }
    this.#carousel.addEventListener('gesturestart', (event) => {
      if (this.closest('product-gallery')?.hasAttribute('allow-zoom') && event.target.closest('[data-nwp-gallery-zoom]:not([hidden])')) event.preventDefault();
    }, { ...options, passive: false });
    window.addEventListener('pageshow', schedule, options);
    this.#observer = new ResizeObserver(schedule);
    this.#observer.observe(this.#carousel);
    this.#mutations = new MutationObserver(schedule);
    this.#mutations.observe(this.#carousel, { subtree: true, attributes: true, attributeFilter: ['hidden'], childList: true });
    this.#mutations.observe(this.closest('product-gallery'), { attributes: true, attributeFilter: ['allow-zoom'] });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#selected = null;
    this.#requested = null;
    this.#pinch = null;
    this.#lightbox?.off('openingAnimationEnd', this.#onViewerOpen);
    this.#lightbox?.off('close', this.#onViewerClose);
    this.#lightbox = null;
    this.#viewerOpen = false;
  }

  #schedule() {
    if (!this.isConnected || this.#frame != null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#synchronize();
    });
  }

  #synchronize() {
    const carousel = this.#carousel;
    const cells = carousel.cells;
    const selected = carousel.selectedCell;
    if (this.#requested && !cells.includes(this.#requested)) this.#requested = null;
    if (this.#requested && selected !== this.#requested) {
      carousel.select(cells.indexOf(this.#requested), { instant: true });
      return;
    }
    if (cells.length && !selected) {
      carousel.select(Math.min(carousel.selectedIndex, cells.length - 1), { instant: true });
      return;
    }
    if (this.#selected !== selected) {
      if (this.#selected) this.#observer?.unobserve(this.#selected);
      this.#selected = selected;
      if (selected) this.#observer?.observe(selected);
    }
    const height = selected?.getBoundingClientRect().height ?? 0;
    const horizontal = carousel.scrollWidth > carousel.clientWidth + 1;
    const maxHeight = horizontal && height > 0 ? `${height}px` : '';
    if (carousel.style.maxHeight !== maxHeight) carousel.style.maxHeight = maxHeight;

    const gallery = this.closest('product-gallery');
    if (!gallery) return;
    for (const cell of carousel.allCells) {
      const button = cell.querySelector('[data-nwp-gallery-zoom]');
      if (!button) continue;
      const disabled = !gallery.hasAttribute('allow-zoom');
      if (button.hidden !== disabled) button.hidden = disabled;
      const tabIndex = !disabled && cells.includes(cell) && (!horizontal || cell === selected) ? 0 : -1;
      if (button.getAttribute('tabindex') !== String(tabIndex)) button.tabIndex = tabIndex;
    }
    const itemLabel = gallery.dataset.nwpItemLabel;
    const goToLabel = gallery.dataset.nwpGoToLabel;
    const label = (template, index) => template?.replace('[index]', String(index + 1)).replace('[count]', String(cells.length));
    if (itemLabel) cells.forEach((cell, index) => cell.setAttribute('aria-label', label(itemLabel, index)));
    if (goToLabel) {
      gallery.querySelectorAll('carousel-navigation, product-gallery-navigation').forEach((navigation) => {
        [...navigation.children].filter((button) => !button.hidden).forEach((button, index) => {
          button.setAttribute('aria-label', label(goToLabel, index));
        });
      });
    }
    const text = horizontal && selected && itemLabel ? label(itemLabel, cells.indexOf(selected)) : '';
    if (this.textContent !== text) this.textContent = text;
  }

  #openImage(cell, trigger, pinch = false) {
    const gallery = this.closest('product-gallery');
    if (!gallery?.hasAttribute('allow-zoom')) return false;
    const images = this.#carousel.cells.filter((item) => item.dataset.mediaType === 'image');
    const index = images.indexOf(cell);
    if (index < 0) return false;
    // Safari does not focus pointer-activated buttons; give PhotoSwipe a stable return target.
    trigger.focus({ preventScroll: true });
    const lightbox = gallery.lightBox;
    Object.assign(lightbox.options, {
      mainClass: 'nwp-product-lightbox',
      pinchToClose: false,
      closeOnVerticalDrag: false,
      allowPanToNext: false,
      imageClickAction: 'zoom',
      tapAction: 'zoom',
      showHideAnimationType: pinch ? 'none' : 'zoom'
    });
    if (this.#lightbox !== lightbox) {
      this.#lightbox = lightbox;
      this.#onViewerOpen = () => { this.#viewerOpen = true; this.#applyPinch(); };
      this.#onViewerClose = () => { this.#viewerOpen = false; this.#pinch = null; };
      lightbox.on('openingAnimationEnd', this.#onViewerOpen);
      lightbox.on('close', this.#onViewerClose);
    }
    gallery.dispatchEvent(new CustomEvent('lightbox:open', { detail: { index } }));
    return true;
  }

  #touchGeometry(touches) {
    const [a, b = a] = touches;
    return {
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
      distance: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
    };
  }

  #onTouchStart(event) {
    if (event.touches.length !== 2) return;
    if (this.#pinch?.ended) this.#pinch = null;
    if (this.#pinch) {
      event.preventDefault();
      const slide = this.#lightbox?.pswp?.currSlide;
      if (slide) this.#pinch.ratio = slide.currZoomLevel / slide.zoomLevels.initial;
      this.#pinch.start = this.#touchGeometry(event.touches);
      this.#pinch.current = this.#pinch.start;
      this.#pinch.previous = this.#pinch.start;
      this.#pinch.baseRatio = this.#pinch.ratio;
      this.#pinch.panning = false;
      if (slide) {
        this.#pinch.imagePoint = { x: (this.#pinch.current.x - slide.pan.x) / (slide.width * slide.currZoomLevel), y: (this.#pinch.current.y - slide.pan.y) / (slide.height * slide.currZoomLevel) };
      }
      return;
    }
    const trigger = event.target.closest('[data-nwp-gallery-zoom]:not([hidden])');
    const gallery = this.closest('product-gallery');
    if (!trigger || !gallery?.hasAttribute('allow-zoom') || gallery.lightBox.pswp) return;
    // Own only a pinch that starts on an enabled image. Single-finger page scrolling
    // and carousel swiping, and browser magnification elsewhere, remain native.
    event.preventDefault();
    const point = this.#touchGeometry(event.touches);
    const image = trigger.parentElement.querySelector(':scope > img').getBoundingClientRect();
    const imagePoint = { x: (point.x - image.left) / image.width, y: (point.y - image.top) / image.height };
    this.#pinch = { start: point, current: point, previous: point, imagePoint, initialized: false, panning: false, ratio: 1, baseRatio: 1, ended: false };
    if (!this.#openImage(trigger.closest('.product-gallery__media'), trigger, true)) this.#pinch = null;
  }

  #onTouchMove(event) {
    const pinch = this.#pinch;
    if (!pinch || pinch.ended || !event.touches.length) return;
    event.preventDefault();
    const point = this.#touchGeometry(event.touches);
    if (event.touches.length === 1 && pinch.singlePoint) {
      pinch.current = { ...pinch.current, x: pinch.current.x + point.x - pinch.singlePoint.x, y: pinch.current.y + point.y - pinch.singlePoint.y };
      pinch.singlePoint = point;
    } else {
      pinch.current = point;
    }
    if (event.touches.length === 2 && pinch.start.distance > 0) {
      pinch.ratio = pinch.baseRatio * pinch.current.distance / pinch.start.distance;
    }
    this.#applyPinch();
  }

  #onTouchEnd(event) {
    const pinch = this.#pinch;
    if (!pinch || pinch.ended) return;
    event.preventDefault();
    if (event.touches.length) {
      // Lifting one finger is not motion: retain the last two-finger focal point,
      // including when the engine is still loading. Only actual movement pans.
      pinch.singlePoint = this.#touchGeometry(event.touches);
      pinch.panning = true;
      // A remaining finger can pan; a later second finger starts from this scale.
      pinch.start = { ...pinch.current, distance: 0 };
    } else {
      pinch.ended = true;
    }
    this.#applyPinch();
  }

  #applyPinch() {
    const pinch = this.#pinch;
    const viewer = this.#lightbox?.pswp;
    const slide = viewer?.currSlide;
    if (!pinch || !slide || !this.#viewerOpen) return;
    // Use PhotoSwipe's public zoom/pan methods so its image bounds and resolution
    // selection remain authoritative while the original touch targets are held.
    viewer.zoomTo(slide.zoomLevels.initial * pinch.ratio, pinch.previous, 0);
    if (!pinch.panning || !pinch.initialized) {
      // Map the original thumbnail pixel to the fingers in the viewer, whose
      // fitted image may occupy a different vertical position on the screen.
      slide.panTo(pinch.current.x - pinch.imagePoint.x * slide.width * slide.currZoomLevel, pinch.current.y - pinch.imagePoint.y * slide.height * slide.currZoomLevel);
      pinch.initialized = true;
    } else {
      slide.panTo(slide.pan.x + pinch.current.x - pinch.previous.x, slide.pan.y + pinch.current.y - pinch.previous.y);
    }
    pinch.previous = pinch.current;
    if (pinch.ended) this.#pinch = null;
  }

  #onKeydown(event) {
    const carousel = this.#carousel;
    if (event.target !== carousel || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const gallery = this.closest('product-gallery');
    if ((event.key === 'Enter' || event.key === ' ') && gallery?.hasAttribute('allow-zoom') && carousel.selectedCell?.dataset.mediaType === 'image') {
      event.preventDefault();
      this.#openImage(carousel.selectedCell, carousel);
      return;
    }
    if (carousel.scrollWidth <= carousel.clientWidth + 1) return;
    const rtl = getComputedStyle(carousel).direction === 'rtl';
    let index = carousel.selectedIndex;
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) index++;
    else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) index--;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = carousel.cells.length - 1;
    else return;
    event.preventDefault();
    carousel.select(Math.max(0, Math.min(index, carousel.cells.length - 1)), { instant: true });
  }
}

if (!customElements.get('nwp-product-gallery-controller')) {
  customElements.define('nwp-product-gallery-controller', NwpProductGalleryController);
}
